/**
 * Utility functions for handling Firestore timestamps with i18n support
 */

// Convert Firestore timestamp to ISO string
export const timestampToISOString = (timestamp: any): string | null => {
  if (!timestamp) return null

  // Handle Firestore Timestamp objects
  if (timestamp.toDate && typeof timestamp.toDate === "function") {
    return timestamp.toDate().toISOString()
  }

  // Handle timestamp as seconds and nanoseconds
  if (timestamp._seconds !== undefined && timestamp._nanoseconds !== undefined) {
    return new Date(timestamp._seconds * 1000).toISOString()
  }

  // Handle timestamp as Date object
  if (timestamp instanceof Date) {
    return timestamp.toISOString()
  }

  // Handle timestamp as ISO string
  if (typeof timestamp === "string") {
    return timestamp
  }

  // Handle timestamp as number (milliseconds)
  if (typeof timestamp === "number") {
    return new Date(timestamp).toISOString()
  }

  return null
}

// Get current locale from browser or default
const getCurrentLocale = (): string => {
  // Try to get language from localStorage (where the translation hook stores it)
  if (typeof window !== "undefined") {
    const storedLanguage = localStorage.getItem("language")
    if (storedLanguage) {
      const localeMap: Record<string, string> = {
        en: "en-US",
        el: "el-GR",
      }
      return localeMap[storedLanguage] || "en-US"
    }
  }

  // Fallback to browser language
  if (typeof navigator !== "undefined") {
    return navigator.language || "en-US"
  }

  return "en-US"
}

// Format date for display with automatic locale detection
export const formatDate = (timestamp: any): string => {
  const isoString = timestampToISOString(timestamp)
  if (!isoString) return "N/A"

  const date = new Date(isoString)
  const locale = getCurrentLocale()

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

// Format date only (without time) with automatic locale detection
export const formatDateOnly = (timestamp: any): string => {
  const isoString = timestampToISOString(timestamp)
  if (!isoString) return "N/A"

  const date = new Date(isoString)
  const locale = getCurrentLocale()

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date)
}

// Format time only with automatic locale detection
export const formatTimeOnly = (timestamp: any): string => {
  const isoString = timestampToISOString(timestamp)
  if (!isoString) return "N/A"

  const date = new Date(isoString)
  const locale = getCurrentLocale()

  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

// Format relative time (e.g., "2 hours ago") with automatic locale detection
export const formatRelativeTime = (timestamp: any): string => {
  const isoString = timestampToISOString(timestamp)
  if (!isoString) return "N/A"

  const date = new Date(isoString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  const locale = getCurrentLocale()

  // Use Intl.RelativeTimeFormat for modern browsers
  if (typeof Intl !== "undefined" && Intl.RelativeTimeFormat) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })

    if (diffInSeconds < 60) {
      return rtf.format(-diffInSeconds, "second")
    } else if (diffInSeconds < 3600) {
      return rtf.format(-Math.floor(diffInSeconds / 60), "minute")
    } else if (diffInSeconds < 86400) {
      return rtf.format(-Math.floor(diffInSeconds / 3600), "hour")
    } else if (diffInSeconds < 2592000) {
      return rtf.format(-Math.floor(diffInSeconds / 86400), "day")
    } else if (diffInSeconds < 31536000) {
      return rtf.format(-Math.floor(diffInSeconds / 2592000), "month")
    } else {
      return rtf.format(-Math.floor(diffInSeconds / 31536000), "year")
    }
  }

  // Fallback for older browsers
  const isGreek = locale.startsWith("el")

  if (diffInSeconds < 60) {
    return isGreek ? "μόλις τώρα" : "just now"
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return isGreek ? `πριν ${minutes} λεπτά` : `${minutes} minutes ago`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return isGreek ? `πριν ${hours} ώρες` : `${hours} hours ago`
  } else {
    return formatDateOnly(timestamp)
  }
}

// Recursively serialize Firestore data with timestamps
export const serializeFirestoreData = (data: any): any => {
  if (!data) return data

  if (Array.isArray(data)) {
    return data.map((item) => serializeFirestoreData(item))
  }

  if (typeof data === "object") {
    // Check if it's a timestamp
    if (data.toDate && typeof data.toDate === "function") {
      return timestampToISOString(data)
    }

    // Check if it's a timestamp with seconds and nanoseconds
    if (data._seconds !== undefined && data._nanoseconds !== undefined) {
      return timestampToISOString(data)
    }

    // Process regular objects
    const result: Record<string, any> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = serializeFirestoreData(data[key])
      }
    }
    return result
  }

  return data
}
